import {
  Box,
  Button,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useCallback, useEffect, useState } from 'react'

import { Toggle } from '@mangovoice/mango-ui'

import type { SystemSettings, SystemSettingsTranslateFn } from '../types'
import { text } from '../types'

export interface RecordingSettingsTabProps {
  settings: SystemSettings
  onSave: (payload: Partial<SystemSettings>) => Promise<void>
  isSaving: boolean
  t?: SystemSettingsTranslateFn
}

export const RecordingSettingsTab = ({
  settings,
  onSave,
  isSaving,
  t,
}: RecordingSettingsTabProps) => {
  const [inbound, setInbound] = useState(settings.enable_inbound_recording)
  const [outbound, setOutbound] = useState(settings.enable_outbound_recording)
  const [internal, setInternal] = useState(settings.enable_internal_recording)

  useEffect(() => {
    setInbound(settings.enable_inbound_recording)
    setOutbound(settings.enable_outbound_recording)
    setInternal(settings.enable_internal_recording)
  }, [settings])

  const isDirty =
    inbound !== settings.enable_inbound_recording ||
    outbound !== settings.enable_outbound_recording ||
    internal !== settings.enable_internal_recording

  const handleSave = useCallback(async () => {
    await onSave({
      enable_inbound_recording: inbound,
      enable_outbound_recording: outbound,
      enable_internal_recording: internal,
    })
  }, [inbound, outbound, internal, onSave])

  return (
    <VStack gap={8} align="stretch">
      <Box>
        <Heading fontSize="lg" mb={4}>
          {text(t, 'systemSettings.recording.title', 'Call Recording')}
        </Heading>

        <VStack gap={4} align="stretch">
          <Toggle
            checked={inbound}
            onCheckedChange={(details) => setInbound(details.checked)}
          >
            {text(
              t,
              'systemSettings.recording.enableInbound',
              'Enable Inbound Call Recording',
            )}
          </Toggle>

          <Toggle
            checked={outbound}
            onCheckedChange={(details) => setOutbound(details.checked)}
          >
            {text(
              t,
              'systemSettings.recording.enableOutbound',
              'Enable Outbound Call Recording',
            )}
          </Toggle>

          <Toggle
            checked={internal}
            onCheckedChange={(details) => setInternal(details.checked)}
          >
            {text(
              t,
              'systemSettings.recording.enableInternal',
              'Enable Internal Call Recording',
            )}
          </Toggle>
        </VStack>
      </Box>

      <HStack gap={3}>
        <Button
          colorPalette="brand"
          onClick={handleSave}
          borderRadius="lg"
          disabled={isSaving || !isDirty}
        >
          {isSaving ? (
            <Spinner size="sm" />
          ) : (
            <Text>{text(t, 'systemSettings.common.save', 'Save Changes')}</Text>
          )}
        </Button>
        {isDirty && (
          <Text fontSize="sm" color="fg.warning" fontWeight="medium">
            {text(t, 'systemSettings.common.unsavedChanges', 'Unsaved changes')}
          </Text>
        )}
      </HStack>
    </VStack>
  )
}
